export const ARENA_WIDTH = 13;
export const ARENA_HEIGHT = 11;
export const TICK_RATE = 60;
export const POSITION_SCALE = 256;

export type Seed = number | string;
export type PlayerId = string;

export interface Cell {
  readonly x: number;
  readonly y: number;
}

export type Direction = "up" | "down" | "left" | "right";

export type TileType = "floor" | "indestructible" | "destructible";

export const TileType = {
  Floor: "floor" as TileType,
  Indestructible: "indestructible" as TileType,
  Destructible: "destructible" as TileType,
};

export interface ArenaMap {
  readonly width: number;
  readonly height: number;
  readonly tiles: readonly (readonly TileType[])[];
}

export type ItemType =
  "bomb" | "fire" | "speed" | "kick" | "remote" | "pierce" | "shield";

export const ItemType = {
  Bomb: "bomb" as ItemType,
  Fire: "fire" as ItemType,
  Speed: "speed" as ItemType,
  Kick: "kick" as ItemType,
  Remote: "remote" as ItemType,
  Pierce: "pierce" as ItemType,
  Shield: "shield" as ItemType,
};

export interface GameRules {
  readonly rulesetVersion: string;
  readonly contentVersion: string;
  readonly tickRate: number;
  readonly arenaWidth: number;
  readonly arenaHeight: number;
  readonly maxPlayers: number;
  readonly bombFuseTicks: number;
  readonly flameLifetimeTicks: number;
  readonly defaultBombRange: number;
  readonly defaultBombCapacity: number;
  readonly defaultPlayerSpeed: number;
  readonly speedIncrement: number;
  readonly maxSpeedLevel: number;
  readonly maxBombCapacity: number;
  readonly maxBombRange: number;
  readonly spawnProtectionTicks: number;
  readonly shieldInvulnerabilityTicks: number;
  readonly roundTicks: number;
  readonly suddenDeathIntervalTicks: number;
  readonly itemDropChance: number;
  readonly destructibleDensity: number;
  readonly enableSuddenDeath: boolean;
}

export const DEFAULT_RULES: GameRules = Object.freeze({
  rulesetVersion: "neon-fuse-rules-1",
  contentVersion: "neon-fuse-content-1",
  tickRate: TICK_RATE,
  arenaWidth: ARENA_WIDTH,
  arenaHeight: ARENA_HEIGHT,
  maxPlayers: 8,
  bombFuseTicks: 180,
  flameLifetimeTicks: 24,
  defaultBombRange: 2,
  defaultBombCapacity: 1,
  defaultPlayerSpeed: 16,
  speedIncrement: 4,
  maxSpeedLevel: 5,
  maxBombCapacity: 8,
  maxBombRange: 8,
  spawnProtectionTicks: 30,
  shieldInvulnerabilityTicks: 30,
  roundTicks: TICK_RATE * 180,
  suddenDeathIntervalTicks: 30,
  itemDropChance: 0.35,
  destructibleDensity: 0.58,
  enableSuddenDeath: true,
});

export interface MapGenerationOptions {
  readonly width?: number;
  readonly height?: number;
  readonly playerCount?: number;
  readonly destructibleDensity?: number;
  readonly spawnPoints?: readonly Cell[];
  readonly clearRadius?: number;
}

export interface PlayerConfig {
  readonly id: PlayerId;
  readonly name?: string;
  readonly spawn?: Cell;
  readonly bombPass?: boolean;
  readonly linePlacement?: boolean;
  readonly punch?: boolean;
}

export interface ItemPlacement {
  readonly type: ItemType;
  readonly cell: Cell;
  readonly id?: string;
}

export interface GameConfig {
  readonly seed?: Seed;
  readonly map?: ArenaMap;
  readonly mapOptions?: MapGenerationOptions;
  readonly rules?: Partial<GameRules>;
  readonly players?: readonly PlayerConfig[];
  readonly playerCount?: number;
  readonly initialItems?: readonly ItemPlacement[];
  readonly contentVersion?: string;
  readonly rulesetVersion?: string;
}

/** Human and bot clients share this command shape. Missing buttons mean false. */
export interface InputCommand {
  readonly up?: boolean;
  readonly down?: boolean;
  readonly left?: boolean;
  readonly right?: boolean;
  readonly direction?: Direction;
  readonly move?: Direction;
  readonly bomb?: boolean;
  readonly action?: boolean;
  readonly detonate?: boolean;
  readonly kick?: boolean;
  readonly punch?: boolean;
  readonly line?: boolean;
}

export interface NormalizedInputCommand {
  readonly up: boolean;
  readonly down: boolean;
  readonly left: boolean;
  readonly right: boolean;
  readonly bomb: boolean;
  readonly detonate: boolean;
  readonly kick: boolean;
  readonly punch: boolean;
  readonly line: boolean;
}

export type InputFrame = Readonly<Record<PlayerId, InputCommand>>;

export interface PlayerInput {
  readonly playerId: PlayerId;
  readonly command: InputCommand;
}

export interface TickInputFrame {
  readonly tick: number;
  readonly inputs: InputFrame;
}

export interface ReplayTick {
  readonly tick: number;
  readonly inputs: InputFrame;
}

export type RoundStatus = "running" | "won" | "draw";

export interface PlayerSnapshot {
  readonly id: PlayerId;
  readonly name?: string;
  /** Position in logical tile coordinates. Values between tile centers are valid. */
  readonly x: number;
  readonly y: number;
  readonly position: Cell;
  readonly previousPosition: Cell;
  readonly cell: Cell;
  readonly alive: boolean;
  readonly health: number;
  readonly deathTick?: number;
  readonly facing: Direction;
  readonly speedLevel: number;
  readonly speed: number;
  readonly maxBombs: number;
  readonly activeBombs: number;
  readonly bombsAvailable: number;
  readonly bombRange: number;
  readonly kick: boolean;
  readonly remote: boolean;
  readonly pierce: boolean;
  readonly shield: boolean;
  readonly bombPass: boolean;
  readonly linePlacement: boolean;
  readonly punch: boolean;
  readonly invulnerableTicks: number;
  readonly spawnProtectionTicks: number;
  readonly score: number;
}

export interface BombSnapshot {
  readonly id: number;
  readonly ownerId: PlayerId;
  readonly cell: Cell;
  readonly fuseRemaining: number;
  readonly range: number;
  readonly remote: boolean;
  readonly pierce: boolean;
  readonly placedTick: number;
}

export interface FlameSnapshot {
  readonly cell: Cell;
  readonly remaining: number;
  readonly sourceBombId?: number;
}

export interface ItemSnapshot {
  readonly id: string;
  readonly type: ItemType;
  readonly cell: Cell;
}

export type GameEventType =
  | "bomb-placed"
  | "bomb-kicked"
  | "bomb-detonated"
  | "wall-destroyed"
  | "item-spawned"
  | "item-collected"
  | "player-damaged"
  | "player-died"
  | "round-started"
  | "sudden-death-started"
  | "sudden-death-cell"
  | "round-ended";

export interface GameEvent {
  readonly tick: number;
  readonly type: GameEventType;
  readonly playerId?: PlayerId;
  readonly ownerId?: PlayerId;
  readonly bombId?: number;
  readonly itemId?: string;
  readonly itemType?: ItemType;
  readonly cell?: Cell;
  readonly reason?: string;
}

export interface GameSnapshot {
  readonly tick: number;
  readonly seed: number;
  readonly rulesetVersion: string;
  readonly contentVersion: string;
  readonly map: ArenaMap;
  readonly players: readonly PlayerSnapshot[];
  readonly bombs: readonly BombSnapshot[];
  readonly flames: readonly FlameSnapshot[];
  readonly items: readonly ItemSnapshot[];
  readonly suddenDeathHazards: readonly Cell[];
  readonly status: RoundStatus;
  readonly winnerId?: PlayerId;
  readonly roundTicksRemaining: number;
  readonly suddenDeath: boolean;
  readonly rngState: number;
  readonly events: readonly GameEvent[];
  readonly replayHash: string;
}

export interface ReplayData {
  readonly formatVersion: 1;
  readonly rulesetVersion: string;
  readonly contentVersion: string;
  readonly seed: number;
  readonly map: ArenaMap;
  readonly rules: GameRules;
  readonly players: readonly PlayerConfig[];
  readonly initialItems: readonly ItemPlacement[];
  readonly inputs: readonly ReplayTick[];
  readonly events: readonly GameEvent[];
  readonly finalHash: string;
}

export interface InvariantViolation {
  readonly code: string;
  readonly message: string;
  readonly path?: string;
}
